import { useRef, useState, useEffect, useCallback } from 'react';
import { Modal, DatePicker, Button, Table, Spin, message, InputNumber, Input, Form } from 'antd';
import { FileDoneOutlined, PrinterOutlined, FilePdfOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../api/axios';

const MONTHS = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'];

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export default function PayrollReportModal({ open, onClose }) {
  const printRef = useRef();
  const [period, setPeriod] = useState(dayjs());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // To'lash modali
  const [payRow, setPayRow] = useState(null);
  const [payForm] = Form.useForm();
  const [paying, setPaying] = useState(false);

  const fetchData = useCallback((p) => {
    setLoading(true);
    api.get(`/employees/payroll-summary?month=${p.month() + 1}&year=${p.year()}`)
      .then(res => setData(res.data?.data || null))
      .catch(() => message.error('Vedomost yuklanmadi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) { const p = dayjs(); setPeriod(p); fetchData(p); }
  }, [open, fetchData]);

  const openPay = (row) => {
    setPayRow(row);
    payForm.setFieldsValue({ amount: row.remaining, note: '' });
  };

  const submitPay = () => {
    payForm.validateFields().then(v => {
      setPaying(true);
      api.post(`/employees/${payRow.id}/salary-payment`, {
        month: data.month, year: data.year, amount: v.amount, note: v.note || null,
      }).then(() => {
        message.success('To\'lov yozildi');
        setPayRow(null);
        fetchData(period);
      }).catch(err => message.error(err.response?.data?.message || 'Xatolik'))
        .finally(() => setPaying(false));
    });
  };

  const t = data?.totals;
  const periodLabel = data ? `${MONTHS[(data.month - 1) % 12]} ${data.year}` : '';

  const columns = [
    { title: '#', key: 'i', width: 40, render: (_, __, i) => i + 1 },
    { title: 'Xodim', dataIndex: 'fullname', render: (v, r) => (
      <div><b>{v}</b>{r.position_name && <div style={{ fontSize: 11, color: 'var(--qk-text-muted,#999)' }}>{r.position_name}</div>}</div>
    ) },
    { title: 'Asosiy', dataIndex: 'base_salary', align: 'right', render: v => formatPrice(v) },
    { title: 'Daromad', dataIndex: 'order_earnings', align: 'right',
      render: (v, r) => <span>{formatPrice(v)}{r.order_count > 0 && <span style={{ fontSize: 11, color: 'var(--qk-text-muted,#999)' }}> ({r.order_count})</span>}</span> },
    { title: 'JAMI', dataIndex: 'total', align: 'right', render: v => <b style={{ color: '#667eea' }}>{formatPrice(v)}</b> },
    { title: "To'langan", dataIndex: 'paid', align: 'right', render: v => <span style={{ color: '#22c55e' }}>{formatPrice(v)}</span> },
    { title: 'Qoldiq', dataIndex: 'remaining', align: 'right',
      render: v => v > 0 ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>{formatPrice(v)}</span> : <span style={{ color: '#22c55e' }}>0</span> },
    { title: '', key: 'act', width: 90, align: 'center',
      render: (_, r) => (
        <Button size="small" type="primary" ghost icon={<DollarOutlined />}
          disabled={r.remaining <= 0} onClick={() => openPay(r)}>To'lash</Button>
      ) },
  ];

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open('', '_blank', 'width=1000,height=700');
    win.document.write(`
      <html><head><title>Maosh vedomosti — ${periodLabel}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:"Times New Roman",serif;padding:30px;color:#111;}
        h1{text-align:center;font-size:17px;margin-bottom:4px;}
        .sub{text-align:center;color:#555;font-size:13px;margin-bottom:16px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;font-size:12.5px;}
        th,td{border:1px solid #999;padding:6px 8px;}
        th{background:#f0f0f0;}
        td.r,th.r{text-align:right;}
        tr.total td{font-weight:bold;background:#f7f7f7;}
        .sign{display:flex;justify-content:space-between;margin-top:50px;font-size:13px;}
        @page{size:A4 landscape;margin:14mm;}
      </style></head><body>${content.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={940}
      title={<span><FileDoneOutlined style={{ color: '#667eea', marginRight: 8 }} />Maosh vedomosti</span>}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <DatePicker picker="month" value={period} allowClear={false} format="MMMM YYYY"
            onChange={(p) => { setPeriod(p); fetchData(p); }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={onClose}>Yopish</Button>
            <Button icon={<PrinterOutlined />} onClick={handlePrint} disabled={!data}>Chop etish</Button>
            <Button type="primary" icon={<FilePdfOutlined />} onClick={handlePrint} disabled={!data}
              style={{ background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)', border: 'none' }}>PDF</Button>
          </div>
        </div>
      }
      styles={{ body: { maxHeight: '64vh', overflowY: 'auto' } }}
    >
      {loading || !data ? (
        <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
      ) : (
        <>
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={data.rows}
            pagination={false}
            summary={() => t && (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: 'var(--qk-bg-soft)', fontWeight: 700 }}>
                  <Table.Summary.Cell index={0} colSpan={2}>JAMI ({data.rows.length} xodim)</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">{formatPrice(t.base_salary)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{formatPrice(t.order_earnings)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right"><b style={{ color: '#667eea' }}>{formatPrice(t.total)}</b></Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right"><span style={{ color: '#22c55e' }}>{formatPrice(t.paid)}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right"><span style={{ color: '#f59e0b' }}>{formatPrice(t.remaining)}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={7} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />

          {/* Chop etish uchun yashirin tarkib */}
          <div style={{ display: 'none' }}>
            <div ref={printRef}>
              <h1>MAOSH VEDOMOSTI</h1>
              <div className="sub">{data.business || ''} — {periodLabel}</div>
              <table>
                <thead>
                  <tr>
                    <th>#</th><th>Xodim</th><th>Lavozim</th>
                    <th className="r">Asosiy maosh</th><th className="r">Buyurtma daromadi</th>
                    <th className="r">Jami</th><th className="r">To'langan</th><th className="r">Qoldiq</th><th>Imzo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td><td>{r.fullname}</td><td>{r.position_name || '—'}</td>
                      <td className="r">{formatPrice(r.base_salary)}</td>
                      <td className="r">{formatPrice(r.order_earnings)}</td>
                      <td className="r">{formatPrice(r.total)}</td>
                      <td className="r">{formatPrice(r.paid)}</td>
                      <td className="r">{formatPrice(r.remaining)}</td>
                      <td></td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td colSpan={3}>JAMI ({data.rows.length} xodim)</td>
                    <td className="r">{formatPrice(t.base_salary)}</td>
                    <td className="r">{formatPrice(t.order_earnings)}</td>
                    <td className="r">{formatPrice(t.total)}</td>
                    <td className="r">{formatPrice(t.paid)}</td>
                    <td className="r">{formatPrice(t.remaining)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <div className="sign">
                <div>Rahbar: ______________</div>
                <div>Bosh hisobchi: ______________</div>
                <div>Sana: {new Date().toLocaleDateString('uz-UZ')}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* To'lash modali */}
      <Modal
        open={!!payRow}
        onCancel={() => setPayRow(null)}
        onOk={submitPay}
        okText="To'lash"
        confirmLoading={paying}
        forceRender
        title={payRow ? `${payRow.fullname} — maosh to'lovi` : "Maosh to'lovi"}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <span>Jami: <b>{formatPrice(payRow?.total)} so'm</b></span>
          <span>Qoldiq: <b style={{ color: '#f59e0b' }}>{formatPrice(payRow?.remaining)} so'm</b></span>
        </div>
        <Form form={payForm} layout="vertical" requiredMark={false}>
          <Form.Item name="amount" label="To'lov summasi (so'm)"
            rules={[{ required: true, message: 'Summa kiriting' },
              { type: 'number', min: 1, message: 'Summa > 0' }]}>
            <InputNumber min={1} style={{ width: '100%' }} size="large"
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={v => v.replace(/\s/g, '')} />
          </Form.Item>
          <Form.Item name="note" label="Izoh (ixtiyoriy)">
            <Input placeholder="masalan: Avans / To'liq maosh" />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}
